<script setup>
import { ref, computed, onMounted } from 'vue';
import { format, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios'
import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?worker'
import { createToast } from '@/assets/js/myToast.js'

// Existing users
const users = ref([]);
const allOrganisations = ref([]);
const allCertifications = ref([]);

// Form state
const showCreateForm = ref(false);
const editingUserId = ref(null);


// PDF Preview state
const showPdfPreview = ref(false);
const previewPdf = ref(null);
const pdfViewerContainer = ref(null);
const pdfLoadingError = ref(false);
const pdfLoading = ref(false);

// New user form data
const newUser = ref({
  username: '',
  email: '',
  validityType: 'days', // 'days' or 'date'
  validityDays: 14,
  validityDate: null,
  assignedCertificationNames: []
});

// Search and filter
const searchQuery = ref('');
const statusFilter = ref('all'); // 'all', 'active', 'expired'

// Filtered users
const filteredUsers = computed(() => {
  let result = [...users.value];
  
  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(user => 
      user.username.toLowerCase().includes(query) || 
      user.email.toLowerCase().includes(query)
    );
  }
  
  // Apply status filter
  if (statusFilter.value !== 'all') {
    const now = new Date();
    result = result.filter(user => {
      const isExpired = new Date(user.registration_valid_until) < now;
      if (statusFilter.value === 'active') {
        return user.is_active && !isExpired;
      } else if (statusFilter.value === 'expired') {
        return isExpired;
      }
      return true;
    });
  }
  
  return result;
});


// Show create form
function showCreateUserForm() {
  resetForm();
  showCreateForm.value = true;
  editingUserId.value = null;
  
}

// Show edit form
function editUser(user) {
  newUser.value = {
    username: user.username,
    email: user.email,
    organisation: user.organisation,
    validityType: 'date',
    validityDays: calcValidityDays(user.registration_valid_until),
    validityDate: user.registration_valid_until,
    assignedCertificationNames: user.user_pdfs.map(pdf => pdf.pdf_name)
  };
  
  showCreateForm.value = true;
  editingUserId.value = user.id;
  
 }

 function calcValidityDays(registration_valid_until){
    console.log('entry calcValidityDays', registration_valid_until)

    let validityDays = Math.round((new Date(registration_valid_until) - new Date()) / (1000 * 3600 * 24))

    if(validityDays > 0){
      return validityDays
    }else{
      return 0
    }

 }

// Reset form
function resetForm() {
  newUser.value = {
    username: '',
    email: '',
    validityType: 'days',
    validityDays: 14,
    validityDate: null,
    assignedCertificationNames: []
  };

}

// Close form
function closeForm() {
  showCreateForm.value = false;
  editingUserId.value = null;
  resetForm();
}

// Validate form
function validateForm() {
  if (!newUser.value.username.trim()) {
    alert('Bitte geben Sie einen Benutzernamen ein.');
    return false;
  }
  
  if (!newUser.value.email.trim()) {
    alert('Bitte geben Sie eine E-Mail-Adresse ein.');
    return false;
  }
   
  return true;
}

// Save user
function saveUser() {
  if (!validateForm()) return;
  
  const registration_valid_until = addDays(new Date(), newUser.value.validityDays)
  
  const user_pdfs = allCertifications.value.filter(pdf => 
    newUser.value.assignedCertificationNames.includes(pdf.name)
  );
  
  if (editingUserId.value) {
    console.log('wir haben einen existierenden User')
    // Update existing user
    update_certification_user(editingUserId.value, user_pdfs, registration_valid_until)
  } else {
    console.log('neuen user anlegen')
    createUser(newUser, user_pdfs, registration_valid_until)
  }
  
  closeForm();
}

// Toggle PDF assignment
function togglePdfAssignment(pdfId) {
  const index = newUser.value.assignedCertificationNames.indexOf(pdfId);
  if (index === -1) {
    newUser.value.assignedCertificationNames.push(pdfId);
  } else {
    newUser.value.assignedCertificationNames.splice(index, 1);
  }
}

// Close PDF preview
function closePdfPreview() {
  showPdfPreview.value = false;
  previewPdf.value = null;
}

// Check if user is expired
function isUserExpired(user) {
  return new Date(user.registration_valid_until) < new Date();
}

// Get user status
function getUserStatus(user) {
  if (!user.is_active) return 'Deaktiviert';
  if (isUserExpired(user)) return 'Abgelaufen';
  return 'Aktiv';
}

// Get user status class
function getUserStatusClass(user) {
  if (!user.is_active) return 'bg-secondary';
  if (isUserExpired(user)) return 'bg-danger';
  return 'bg-success';
}

// Format date
function formatDate(date) {
  if(date) return format( new Date(date), 'dd.MM.yyyy', { locale: de });
  return null
}

// Calculate days until expiry
function getDaysUntilExpiry(user) {
  const now = new Date();
  const diffTime = new Date(user.registration_valid_until) - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}


// Show PDF preview with PDF.js
function showPdfPreviewModal(pdf_name) {
  console.log('entry showPdfPreviewModal', pdf_name)
  showPdfPreview.value = true;
  pdfLoadingError.value = false;
  pdfLoading.value = true;
  loadCertification(pdf_name)
}

async function renderPdfPreview() {
  console.log('entry renderPdfPreview');
  // Wait for DOM update
  console.log('vor promise')
  await new Promise(resolve => setTimeout(resolve, 100));
  console.log('nach promise')

  try {
    // Load PDF document

    const loadingTask = pdfjsLib.getDocument(previewPdf.value.base64);
    const pdfDoc = await loadingTask.promise;

    console.log('pdfDoc', pdfDoc)
    // Clear container
    if (pdfViewerContainer.value) {
      console.log('entry clearcontainer')
      pdfViewerContainer.value.innerHTML = '';

      // Render first page
      const page = await pdfDoc.getPage(1);
      const scale = 1;
      const viewport = page.getViewport({ scale });

      // Create canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render page
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      console.log('before page.render')
      await page.render(renderContext).promise;
      console.log('after page.render')

      // Add canvas to container
      pdfViewerContainer.value.appendChild(canvas);

      // Add page info
      const pageInfo = document.createElement('div');
      pageInfo.className = 'text-center mt-3 text-secondary';
      pageInfo.innerHTML = `Seite 1 von ${pdfDoc.numPages}`;
      console.log('pageInfo', pageInfo)
      pdfViewerContainer.value.appendChild(pageInfo);
    }

  } catch (error) {
    console.error('Fehler beim Laden der PDF:', error);
    pdfLoadingError.value = true;
  } finally {
    console.log('finally')
    pdfLoading.value = false; 
  }
}

function createUser(user, user_pdfs, registration_valid_until){
  console.log('entry createUser', user.value)
  let user_email = user.value.email
  let user_init_object = {
    'user': user.value,
    'user_pdfs': user_pdfs,
    'registration_valid_until': registration_valid_until
  }
  axios
    .post("/api/v1/account/certifications/create_user/", user_init_object)
    .then(response => {
      console.log('user angelegt, nun initialisieren', response)
      reset_passwort_user(user_email)
      loadUsers()
    })
    .catch(error => {
      console.log('error')
    })
}

function reset_passwort_user(user_email){
  console.log('entry reset_passwort_user', user_email)

  const formData = {
                email: user_email,
            }
  axios
    .post("/api/v1/users/reset_password/", formData)
    .then(response => {
      console.log('passwortreset gesendet', response)
    })
    .catch(error => {
      console.log('error')
    })
  console.log('entry reset_passwort_user')


}

function update_certification_user(user_id, user_pdfs, registration_valid_until){
  console.log('entry update_certification_user', user_id)
  console.log('user_pdfs', user_pdfs)
  let user_init_object = {'user_pdfs': user_pdfs,
    'registration_valid_until': registration_valid_until
  }
  axios
    .post("/api/v1/account/"+user_id+"/update_certification_user/", user_init_object)
    .then(response => {
      console.log('user initialisiert', response)
      loadUsers()
    })
    .catch(error => {
      console.log('error', error)
      deleteUser(user_id) 
    })
  console.log('exit init_certification_user', user_id)
}

function deleteUser(user_id){
  console.log('entry deleteUser', user_id)
  axios
      .delete("/api/v1/users/"+user_id+"/")
      .then(response => {
        console.log('user wieder gelöscht')
        loadUsers()
      })
      .catch(error => {
        console.log('error')
      })
  
}

function loadUsers(){
  axios
    .get('/api/v1/account/certifications/')
    .then(response => {
        users.value = response.data
        console.log('users fertig geladen')
    })
    .catch(error => {
      console.log('error loadUsers')
        if (error.response) {                     
            console.error(JSON.stringify(error.response.data))
        } else if (error.message) {
          console.error(JSON.stringify(error.message))
        } else {
          console.error(JSON.stringify(error))
        }
    })
}

function loadCertifications(){
  axios
    .get('/api/v1/account/certifications/files/')
    .then(response => {
        allCertifications.value = response.data.files
        console.log('pdfs fertig geladen')
    })
    .catch(error => {
      console.log('error loadCertifications')
        if (error.response) {
            if ('detail' in error.response.data){
                createToast({text: this.$t(error.response.data['detail'])
                            ,type: 'error'})
            }                        
            console.error(JSON.stringify(error.response.data))
        } else if (error.message) {
          console.error(JSON.stringify(error.message))
        } else {
          console.error(JSON.stringify(error))
        }
    })
}

function loadCertification(name){
  axios
    .get('/api/v1/account/certifications/files/'+name+'/')
    .then(response => {
      previewPdf.value = response.data;
      console.log('pdf fertig geladen')
      renderPdfPreview()
    })
    .catch(error => {
      console.log('error loadCertifications')
        if (error.response) {                     
            console.error(JSON.stringify(error.response.data))
        } else if (error.message) {
          console.error(JSON.stringify(error.message))
        } else {
          console.error(JSON.stringify(error))
        }
    })
}

function getAllOrganisations(){
  console.log('entry getAllOrganisations')
  axios
    .get('/api/v1/allOrganisations/')
    .then(response => {
      allOrganisations.value = response.data
      console.log('allOrganisations', allOrganisations.value)
      allOrganisations.value = sortOrganisations()
    })
    .catch(error => {
      console.log(error, 'getAllOrganisations')
    })
  console.log('exit getAllOrganisations')
}

function sortOrganisations(){
  var sortedOrganisations = allOrganisations.value.sort((a,b) => {
                
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    }
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    }
      return 0;
    })
  return sortedOrganisations
}

// Cleanup on unmount
onMounted(() => {
  loadUsers()
  getAllOrganisations()
  loadCertifications()
});
</script>

<template>
  <div class="container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1 class="h3 mb-0">Benutzerverwaltung Zertifizierung</h1>
      <button class="btn btn-primary" @click="showCreateUserForm">
        <i class="bi bi-person-plus me-2"></i>
        Neuen Benutzer anlegen
      </button>
    </div>

    <!-- Filters -->
    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input 
                type="text" 
                class="form-control" 
                placeholder="Nach Benutzername oder E-Mail suchen..." 
                v-model="searchQuery"
              />
              <button 
                v-if="searchQuery" 
                class="btn btn-outline-secondary" 
                type="button"
                @click="searchQuery = ''"
              >
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>
          <div class="col-md-3">
            <select class="form-select" v-model="statusFilter">
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="expired">Abgelaufen</option>
            </select>
          </div>
          <div class="col-md-3">
            <div class="d-flex gap-2">
              <span class="badge bg-success">{{ users.filter(u => u.is_active && !isUserExpired(u)).length }} Aktiv</span>
              <span class="badge bg-secondary">{{ users.filter(u => !u.is_active).length}} Inaktiv</span>
              <span class="badge bg-danger">{{ users.filter(u => isUserExpired(u)).length }} Abgelaufen</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Users List -->
    <div class="row g-4">
      <div v-for="user in filteredUsers" :key="user.id" class="col-12">
        <div class="card shadow-sm">
          <div class="card-body">
            <div class="row align-items-center">
              <!-- User Info -->
              <div class="col-md-3">
                <div class="d-flex align-items-center">
                  <div class="me-3">
                    <i class="bi bi-person-circle text-primary display-6"></i>
                  </div>
                  <div>
                    <h5 class="mb-1">{{ user.username }}</h5>
                    <p class="text-secondary mb-0">{{ user.email }}</p>
                  </div>
                </div>
              </div>

              <!-- Status & Validity -->
              <div class="col-md-3">
                <div class="mb-2">
                  <span class="badge" :class="getUserStatusClass(user)">
                    {{ getUserStatus(user) }}
                  </span>
                </div>
                <div class="small text-secondary">
                  <div>Erstellt: {{ formatDate(user.date_joined) }}</div>
                  <div>Gültig bis: {{ formatDate(user.registration_valid_until) }}</div>
                  <div v-if="!isUserExpired(user)" class="text-warning">
                    Noch {{ getDaysUntilExpiry(user) }} Tage
                  </div>
                </div>
              </div>

              <!-- PDF Files -->
              <div class="col-md-4">
                <div class="small text-secondary mb-1">Zugewiesene PDF-Dateien ({{ user.user_pdfs.length }})</div>
                <div class="d-flex flex-wrap gap-1">
                  <span 
                    v-for="pdf in user.user_pdfs.slice(0, 3)" 
                    :key="pdf.id"
                    class="badge bg-light text-dark border cursor-pointer"
                    :title="pdf.pdf_name"
                    @click="showPdfPreviewModal(pdf.pdf_name)"
                  >
                    <i class="bi bi-file-earmark-pdf text-danger me-1"></i>
                    {{ pdf.pdf_name.length > 20 ? pdf.pdf_name.substring(0, 20) + '...' : pdf.pdf_name }}
                  </span>
                  <span v-if="user.user_pdfs.length > 3" class="badge bg-secondary">
                    +{{ user.user_pdfs.length - 3 }} weitere
                  </span>
                </div>
              </div>

              <!-- Actions -->
              <div class="col-md-2">
                <div class="dropdown">
                  <button 
                    class="btn btn-outline-secondary btn-sm dropdown-toggle w-100" 
                    type="button" 
                    data-bs-toggle="dropdown"
                  >
                    Aktionen
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="editUser(user)">
                        <i class="bi bi-pencil me-2"></i>
                        Bearbeiten
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a class="dropdown-item text-danger" href="#" @click.prevent="deleteUser(user.id)">
                        <i class="bi bi-trash me-2"></i>
                        Löschen 
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredUsers.length === 0" class="col-12">
        <div class="text-center py-5">
          <i class="bi bi-people display-1 text-secondary mb-3"></i>
          <h2 class="h4">Keine Benutzer gefunden</h2>
          <p class="text-secondary">
            {{ searchQuery ? 'Keine Benutzer entsprechen Ihren Suchkriterien.' : 'Noch keine Benutzer angelegt.' }}
          </p>
          <button v-if="!searchQuery" class="btn btn-primary" @click="showCreateUserForm">
            <i class="bi bi-person-plus me-2"></i>
            Ersten Benutzer anlegen
          </button>
        </div>
      </div>
    </div>

    <!-- Create/Edit User Modal -->
    <div v-if="showCreateForm" class="modal-backdrop fade show"></div>
    <div v-if="showCreateForm" class="modal d-block" tabindex="-1" @click="closeForm">
      <div class="modal-dialog modal-xl modal-dialog-centered" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingUserId ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen' }}
            </h5>
            <button type="button" class="btn-close" @click="closeForm"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveUser">
              <div class="row g-4">
                <!-- Basic User Info -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header bg-white">
                      <h6 class="mb-0">Benutzerdaten</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="form-label">Benutzername <span class="text-danger">*</span></label>
                        <input 
                          type="text" 
                          class="form-control" 
                          v-model="newUser.username"
                          placeholder="z.B. max.mustermann"
                          required
                        />
                      </div>
                      
                      <div class="mb-3">
                        <label class="form-label">E-Mail <span class="text-danger">*</span></label>
                        <input 
                          type="email" 
                          class="form-control" 
                          v-model="newUser.email"
                          placeholder="beispiel@domain.de"
                          required
                        />
                      </div>

                    </div>
                  </div>
                </div>

                <!-- Validity Settings -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header bg-white">
                      <h6 class="mb-0">Organisation</h6>
                    </div>
                    <div class="card-body">
                      
                      <div class="mb-3">
                        <select class="form-select "aria-label="Default select example" v-model="newUser.organisation">
                          <option  v-for="organisation in allOrganisations" 
                            v-bind:key="organisation.name" v-bind:value="organisation.id">{{organisation.name}}
                          </option>
                        </select>
                      </div>
                      
                      
                    </div>
                    <div class="card-header bg-white">
                      <h6 class="mb-0">Gültigkeitsdauer</h6>
                    </div>
                    <div class="card-body">
                      
                      <div class="mb-3">
                        <label class="form-label">Anzahl Tage</label>
                        <div class="input-group">
                          <input 
                            type="number" 
                            class="form-control" 
                            v-model="newUser.validityDays"
                            min="1"
                            max="365"
                          />
                          <span class="input-group-text">Tage</span>
                        </div>
                        <div class="form-text">
                          Gültig bis: {{ formatDate(addDays(new Date(), newUser.validityDays)) }}
                        </div>
                      </div>
                      
                      
                    </div>
                  </div>
                </div>

                <!-- PDF Assignment -->
                <div class="col-12">
                  <div class="card">
                    <div class="card-header bg-white d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">PDF-Dateien zuordnen</h6>
                    </div>
                    <div class="card-body">
                      <div class="row g-3">
                        <div v-for="certification in allCertifications" :key="certification.name" class="col-md-6 col-lg-4">
                          <div 
                            class="card border cursor-pointer pdf-card"
                            :class="{ 'border-primary bg-primary bg-opacity-10': newUser.assignedCertificationNames.includes(certification.name) }"
                            @click="togglePdfAssignment(certification.name)"
                          >
                            <div class="card-body p-3">
                              <div class="d-flex align-items-center">
                                <div class="form-check me-3">
                                  <input 
                                    class="form-check-input" 
                                    type="checkbox" 
                                    :checked="newUser.assignedCertificationNames.includes(certification.name)"
                                    
                                  />
                                </div>
                                <div class="flex-grow-1">
                                  <div class="d-flex align-items-center mb-1">
                                    <i class="bi bi-file-earmark-pdf text-danger me-2"></i>
                                    <span class="fw-medium">{{ certification.name }}</span>
                                  </div>
                                  <div class="small text-secondary">
                                    {{ certification.size }} • {{ formatDate(certification.uploadedAt) }}
                                  </div>
                                </div>
                                <div class="d-flex gap-1">
                                  <button 
                                    type="button"
                                    class="btn btn-sm btn-outline-info"
                                    @click.stop="showPdfPreviewModal(certification.name)"
                                    title="PDF Vorschau"
                                  >
                                    <i class="bi bi-eye"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div v-if="allCertifications.length === 0" class="col-12">
                          <div class="text-center py-4 text-secondary">
                            <i class="bi bi-file-earmark-pdf display-1 mb-3"></i>
                            <p>Keine PDF-Dateien verfügbar. Laden Sie zuerst Dateien hoch.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div v-if="newUser.assignedCertificationNames.length > 0" class="mt-3 pt-3 border-top">
                        <div class="small text-secondary mb-2">
                          Ausgewählte Dateien ({{ newUser.assignedCertificationNames.length }}):
                        </div>
                        <div class="d-flex flex-wrap gap-2">
                          <span 
                            v-for="certifcationName in newUser.assignedCertificationNames" 
                            :key="certifcationName"
                            class="badge bg-primary"
                          >
                            {{ allCertifications.find(p => p.name === certifcationName)?.name }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeForm">Abbrechen</button>
            <button type="button" class="btn btn-primary" @click="saveUser">
              <i class="bi bi-save me-2"></i>
              {{ editingUserId ? 'Speichern' : 'Benutzer anlegen' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- PDF Preview Modal -->
    <div v-if="showPdfPreview" class="modal-backdrop fade show"></div>
    <div v-if="showPdfPreview" class="modal d-block" tabindex="-1" @click="closePdfPreview">
      <div class="modal-dialog modal-xl modal-dialog-centered" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-file-earmark-pdf text-danger me-2"></i>
              PDF Vorschau: {{ previewPdf?.name }}
            </h5>
            <button type="button" class="btn-close" @click="closePdfPreview"></button>
          </div>
          <div class="modal-body p-0">
            <div class="pdf-preview-container">
              <!-- Loading State -->
              <div v-if="pdfLoading" class="text-center py-5">
                <div class="spinner-border text-primary mb-3" role="status">
                  <span class="visually-hidden">Laden...</span>
                </div>
                <p class="text-secondary">PDF wird geladen...</p>
              </div>
              
              <!-- Error State -->
              <div v-else-if="pdfLoadingError" class="text-center py-5">
                <i class="bi bi-exclamation-triangle text-warning display-1 mb-3"></i>
                <p class="text-secondary">PDF-Vorschau konnte nicht geladen werden.</p>
                <button class="btn btn-primary" @click="showPdfPreviewModal(previewPdf?.name)">
                  Erneut versuchen
                </button>
              </div>
              
              <!-- PDF Viewer Container -->
              <div 
                
                ref="pdfViewerContainer" 
                class="pdf-viewer-content"
                style="min-height: 600px; display: flex; flex-direction: column; align-items: center; justify-content: center;"
              >
                <!-- PDF content will be rendered here -->
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="me-auto">
              <small class="text-secondary">
                Größe: {{ previewPdf?.size }} • Hochgeladen: {{ formatDate(previewPdf?.uploadedAt) }}
              </small>
            </div>
            <button type="button" class="btn btn-secondary" @click="closePdfPreview">Schließen</button>
            <a 
              v-if="previewPdf?.base64"
              :href="previewPdf.base64" 
              :download="previewPdf.name"
              target="_blank" rel="noopener noreferrer"
              class="btn btn-primary"
            >
              <i class="bi bi-download me-2"></i>
              Herunterladen
            </a>
          </div>
        </div>
      </div>
  </div>
  </div>
</template>

<style scoped>

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.cursor-pointer {
  cursor: pointer;
}

.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

/* PDF card selection styling */
.pdf-card.border-primary {
  border-width: 2px !important;
}

.pdf-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1) !important;
}

/* File upload styling */
.btn-outline-primary:hover {
  transform: translateY(-1px);
}

/* PDF Preview styling */
.pdf-preview-container {
  background-color: #f8f9fa;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-preview-container iframe {
  background-color: white;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Badge hover effect */
.badge.cursor-pointer:hover {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}
/* Fix dropdown z-index issues */
.dropdown-menu {
  z-index: 1050;
}

.card {
  position: relative;
  z-index: 1;
}

.card:hover {
  z-index: 2;
}

.dropdown.show {
  z-index: 1051;
}
</style>